import React from 'react';
import { type Icon } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  startIcon?: Icon;
  endIcon?: Icon;
  onStartIconClick?: () => void;
  onEndIconClick?: () => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, startIcon, endIcon, onStartIconClick, onEndIconClick, ...props }, ref) => {
    const StartIcon = startIcon;
    const EndIcon = endIcon;

    return (
      <div className="w-full relative">
        {StartIcon && (
          <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
            {onStartIconClick ? (
              <button
                type="button"
                onClick={onStartIconClick}
                className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                aria-label="Clear start icon"
              >
                <StartIcon size={18} className="text-muted-foreground" />
              </button>
            ) : (
              <StartIcon size={18} className="text-muted-foreground" />
            )}
          </div>
        )}
        <input
          type={type}
          className={cn(
            'flex h-12 w-full rounded-md border border-border bg-transparent py-3 text-base transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            type === 'search' &&
              !!endIcon &&
              'appearance-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden [&::-webkit-search-results-button]:hidden [&::-webkit-search-results-decoration]:hidden',
            startIcon && 'pl-8 pr-4',
            endIcon && 'pl-4 pr-8',
            startIcon && endIcon && 'px-8',
            !startIcon && !endIcon && 'px-4',
            className,
          )}
          ref={ref}
          {...props}
        />
        {EndIcon && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {onEndIconClick ? (
              <button
                type="button"
                onClick={onEndIconClick}
                className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                aria-label="Clear input"
              >
                <EndIcon className="text-muted-foreground" size={18} />
              </button>
            ) : (
              <EndIcon className="text-muted-foreground" size={18} />
            )}
          </div>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
